import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Button,
  FormLayout,
  MultiSelectDropdown,
  MultiSelectStoreDropdown,
  MultiSelectChainDropdown,
  TextInput,
} from 'components/common'
import { MASTER_DATA_FORM_FIELDS, PAGE_MASTER_DATA } from 'utils/constants'
import AggregatorsDropdown from './aggregatorsDropdown'
import {
  fetchMasterDataFreshCategories,
  updateMasterData,
} from 'utils/api/masterDataApi'
import toast from 'react-hot-toast'
import {
  convertTo12HourFormat,
  getDepartment,
  getFamily,
  getSection,
  splitCategoryPath,
} from 'utils/helpers'
import ProductImage from './productImage'
import SwitchField from 'components/bundleProduct/switchField'
import BundleItemsFields from 'components/bundleProduct/bundleItemsFields'
import BundlePricingFields from 'components/bundleProduct/bundlePricingFields'
import {
  BundleItem,
  CatalogueProductOption,
  PRODUCT_TYPE,
} from 'types/bundleProduct'
import { handleError } from 'utils/helpers/handleError'
import { updateMasterDataBundleSchema } from 'utils/schema/bundleProductSchema'

dayjs.extend(utc)

// Bundle pricing dates come back from the API as full ISO 8601 timestamps
// (UTC midnight), but a native `<input type="date">` only accepts a plain
// `YYYY-MM-DD` value — read it back as UTC so the calendar date shown
// matches what was originally saved, regardless of the browser's timezone.
const toDateInputValue = (value?: string | null) =>
  value ? dayjs.utc(value).format('YYYY-MM-DD') : ''

const normalizePricing = (pricing?: Record<string, any>) => ({
  ...pricing,
  specialPriceStartDate: toDateInputValue(pricing?.specialPriceStartDate),
  specialPriceEndDate: toDateInputValue(pricing?.specialPriceEndDate),
  slashPriceStartDate: toDateInputValue(pricing?.slashPriceStartDate),
  slashPriceEndDate: toDateInputValue(pricing?.slashPriceEndDate),
})

const {
  ITEM_NUMBER,
  SAP_MATERIAL_NO,
  ACTIVE_ONLINE,
  BARCODE,
  DEPARTMENT,
  ERP_BRAND,
  ERP_ITEM_DESC,
  ERP_MARKETING_CODE,
  ERP_PACK_INFO,
  ERP_UOM,
  FAMILY,
  HIDE_AGGREGATORS,
  ACTIVATED_STORES,
  IN_STOCK_STORES,
  ONLINE_DESC,
  ONLINE_PACK_INFO,
  QTY_PER_UOM,
  SECTION,
  SUB_FAMILY,
  SAP_DEPARTMENT,
  SAP_SECTION,
  SAP_FAMILY,
  SAP_SUB_FAMILY,
  VAT_CLASS,
  VAT_PERCENTAGE,
  BLOCK_ERP_PRICE_SYNC,
  IS_AVAILABLE_IN_FRESH,
  IS_FRESH_PREPARATION,
  FRESH_CATEGORIES,
  PLU,
  CATEGORY,
  IS_LOOSE_ITEM,
  IS_SCALE_ITEM,
  QUANTITY_STEP,
  UNIT,
  SPECIAL_CATEGORY,
  IS_DISABLE,
  LAST_DISABLE_TIME,
  DELIVEROO_MAX_QUANTITY,
  TALABAT_MAX_QUANTITY,
  IS_THIRD_PARTY_PRODUCT,
  STORES,
  CHAINS,
  CREATED_AT,
  UPDATED_AT,
} = MASTER_DATA_FORM_FIELDS

type Props = {
  catalogue: any
}

const MasterDataUpdateForm = ({ catalogue }: Props) => {
  const navigate = useNavigate()
  const isBundleProduct = catalogue?.productType === PRODUCT_TYPE.BUNDLE

  // The catalogue GET response embeds the child product under
  // bundleItems[].catalogue as an object (not just an id), so both the
  // form's flat catalogue-id values and the rich display info for each
  // row can be derived directly from it without any extra fetch.
  const { bundleItems, initialProducts } = useMemo(() => {
    const items = (catalogue?.bundleItems || []) as ({
      catalogue: string | CatalogueProductOption
    } & Omit<BundleItem, 'catalogue'>)[]

    const products: Record<string, CatalogueProductOption> = {}
    const flattened = items.map((item) => {
      if (item.catalogue && typeof item.catalogue === 'object') {
        products[item.catalogue._id] = item.catalogue
        return {
          ...item,
          catalogue: item.catalogue._id,
          quantity: item.quantity ?? 1,
        }
      }
      return { ...item, quantity: item.quantity ?? 1 }
    })

    return { bundleItems: flattened, initialProducts: products }
  }, [catalogue?.bundleItems])

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(updateMasterDataBundleSchema),
    defaultValues: {
      ...catalogue,
      barcode: catalogue._id,
      dept: getDepartment(catalogue?.dept),
      family: getFamily(catalogue?.family),
      section: getSection(catalogue?.section),
      // The SAP hierarchy is stored as codes too, so it resolves through the
      // same lookup tables as the legacy dept/section/family.
      [SAP_DEPARTMENT.key]: getDepartment(catalogue?.sapDept),
      [SAP_SECTION.key]: getSection(catalogue?.sapSection),
      [SAP_FAMILY.key]: getFamily(catalogue?.sapFamily),
      lastDisableTime: catalogue?.lastDisableTime
        ? convertTo12HourFormat(catalogue?.lastDisableTime)
        : '',
      [CREATED_AT.key]: convertTo12HourFormat(catalogue?.createdAt),
      [UPDATED_AT.key]: convertTo12HourFormat(catalogue?.updatedAt),
      // Filled with names once the Fresh categories load - see the effect below
      [FRESH_CATEGORIES.key]: '',
      bundleItems,
      pricing: normalizePricing(catalogue?.pricing),
    },
  })

  const backToMasterData = () => {
    navigate(PAGE_MASTER_DATA.path)
  }

  // In Stock Stores may only contain stores currently selected in
  // Activated Stores - watch the live selection so the dropdown's own
  // option list narrows immediately as the user edits Activated Stores,
  // rather than only surfacing the mismatch as a submit-time error.
  const selectedActivatedStores: string[] =
    useWatch({
      control,
      name: ACTIVATED_STORES.key,
    }) ?? []

  // The catalogue stores Fresh category ids - resolve them to names for
  // display. View-only: assignments are maintained from the master data
  // import, never from this form.
  useEffect(() => {
    const assignedIds: string[] = catalogue?.freshCategories ?? []
    if (!assignedIds.length) return

    fetchMasterDataFreshCategories()
      .then(({ data }) => {
        const namesById = new Map<string, string>(
          (data?.categories ?? []).map(
            (category: { _id: string; name: string; isActive: boolean }) => [
              category._id,
              category.isActive ? category.name : `${category.name} (inactive)`,
            ],
          ),
        )
        setValue(
          FRESH_CATEGORIES.key,
          assignedIds.map((id) => namesById.get(id) ?? id).join(', '),
        )
      })
      .catch(() => setValue(FRESH_CATEGORIES.key, assignedIds.join(', ')))
  }, [catalogue?.freshCategories, setValue])

  const specialCategoryOptions = useMemo(() => {
    return catalogue?.specialCategoriesConfig
      ? catalogue.specialCategoriesConfig.map((item: string) => ({
          label: item,
          value: item,
          disabled: false,
        })) || []
      : []
  }, [catalogue?.specialCategoriesConfig])

  const handleUpdateMasterData = async (data: any) => {
    const updateToast = toast.loading('Updating Master Data...', {
      duration: Infinity,
    })
    try {
      await updateMasterData(catalogue._id, {
        activeOnline: data.activeOnline,
        isThirdPartyProduct: data.isThirdPartyProduct,
        hideAggregators: data.hideAggregators,
        ...(catalogue?.isThirdPartyProduct
          ? { stores: data.stores, chains: data.chains }
          : {}),
        onlineDesc: data.onlineDesc,
        onlinePackInfo: data.onlinePackInfo,
        blockPriceSync: data.blockPriceSync,
        specialCategories: data?.specialCategories || [],
        activatedStores: data?.activatedStores || [],
        inStockStores: data?.inStockStores || [],
        freshPreparation: data.freshPreparation,
        ...(isBundleProduct
          ? { bundleItems: data.bundleItems, pricing: data.pricing }
          : {}),
      })
      toast.success('Master Data Updated Successfully!', {
        id: updateToast,
        duration: 3000,
      })
      reset(data)
      backToMasterData()
    } catch (error) {
      handleError(
        error,
        updateToast,
        {
          default: 'Failed to update Master Data.',
        },
        5000,
      )
    }
  }

  return (
    <form
      id="master-data-update-form"
      onSubmit={handleSubmit(handleUpdateMasterData)}
    >
      {/* Main Category — view-only breadcrumb, set only by the Catalogue
          Categories import (not part of the update payload). */}
      <div className="mt-3">
        <span className="mb-1 block text-sm text-gray-900">Main Category</span>
        {catalogue?.mainCategory ? (
          <nav
            aria-label="Main category"
            className="flex flex-wrap items-center gap-1"
          >
            {splitCategoryPath(catalogue.mainCategory).map(
              (level: string, i: number, arr: string[]) => (
                <span key={i} className="flex items-center gap-1">
                  <span className="rounded-md border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                    {level}
                  </span>
                  {i < arr.length - 1 && (
                    <span className="text-gray-400">&gt;</span>
                  )}
                </span>
              ),
            )}
          </nav>
        ) : (
          <span className="text-sm text-gray-500">—</span>
        )}
      </div>

      <FormLayout>
        <div className="grid grid-cols-1 gap-x-14 gap-y-3 md:grid-cols-3">
          <div className="flex items-center justify-center md:row-span-2">
            <ProductImage
              src={catalogue?.image}
              alt={catalogue?.desc || 'Product'}
              size={176}
              showFallbackIcon
            />
          </div>

          {/* Item Number */}
          <TextInput
            register={register}
            name={ITEM_NUMBER.key}
            label={ITEM_NUMBER.label}
            disabled
          />

          {/* SAP Material Number */}
          <TextInput
            register={register}
            name={SAP_MATERIAL_NO.key}
            label={SAP_MATERIAL_NO.label}
            disabled
          />

          {/* Barcode */}
          <TextInput
            register={register}
            name={BARCODE.key}
            label={BARCODE.label}
            className="text-lg font-semibold"
            disabled
          />
          {/* Department */}
          <TextInput
            name={DEPARTMENT.key}
            label={DEPARTMENT.label}
            register={register}
            disabled
          />
          {/* Section */}
          <TextInput
            name={SECTION.key}
            label={SECTION.label}
            register={register}
            disabled
          />
          {/* Family */}
          <TextInput
            name={FAMILY.key}
            label={FAMILY.label}
            register={register}
            disabled
          />
          {/* Sub Family */}
          <TextInput
            name={SUB_FAMILY.key}
            label={SUB_FAMILY.label}
            register={register}
            disabled
          />
          {/* SAP Department */}
          <TextInput
            name={SAP_DEPARTMENT.key}
            label={SAP_DEPARTMENT.label}
            register={register}
            disabled
          />
          {/* SAP Section */}
          <TextInput
            name={SAP_SECTION.key}
            label={SAP_SECTION.label}
            register={register}
            disabled
          />
          {/* SAP Family */}
          <TextInput
            name={SAP_FAMILY.key}
            label={SAP_FAMILY.label}
            register={register}
            disabled
          />
          {/* SAP Sub Family */}
          <TextInput
            name={SAP_SUB_FAMILY.key}
            label={SAP_SUB_FAMILY.label}
            register={register}
            disabled
          />
          {/* ERP Item Description */}
          <TextInput
            name={ERP_ITEM_DESC.key}
            label={ERP_ITEM_DESC.label}
            register={register}
            disabled
          />
          {/* ERP UOM */}
          <TextInput
            name={ERP_UOM.key}
            label={ERP_UOM.label}
            register={register}
            disabled
          />
          {/* QTY ERP UOM */}
          <TextInput
            register={register}
            name={QTY_PER_UOM.key}
            label={QTY_PER_UOM.label}
            disabled
          />
          {/* ERP Pack Info */}
          <TextInput
            register={register}
            name={ERP_PACK_INFO.key}
            label={ERP_PACK_INFO.label}
            disabled
          />
          {/* ERP Brand */}
          <TextInput
            register={register}
            name={ERP_BRAND.key}
            label={ERP_BRAND.label}
            disabled
          />
          {/* ERP Marketing Code */}
          <TextInput
            register={register}
            name={ERP_MARKETING_CODE.key}
            label={ERP_MARKETING_CODE.label}
            disabled
          />
          {/* Vat Class */}
          <TextInput
            register={register}
            name={VAT_CLASS.key}
            label={VAT_CLASS.label}
            disabled
          />
          {/* Vat Percentage */}
          <TextInput
            register={register}
            name={VAT_PERCENTAGE.key}
            label={VAT_PERCENTAGE.label}
            disabled
          />

          {/* DELIVEROO_MAX_QUANTITY */}
          <TextInput
            disabled
            register={register}
            name={DELIVEROO_MAX_QUANTITY.key}
            label={DELIVEROO_MAX_QUANTITY.label}
          />

          {/* TALABAT_MAX_QUANTITY */}
          <TextInput
            disabled
            register={register}
            name={TALABAT_MAX_QUANTITY.key}
            label={TALABAT_MAX_QUANTITY.label}
          />

          <TextInput
            register={register}
            name={CATEGORY.key}
            label={CATEGORY.label}
            disabled
          />

          <TextInput
            register={register}
            name={IS_LOOSE_ITEM.key}
            label={IS_LOOSE_ITEM.label}
            disabled
          />

          <TextInput
            register={register}
            name={IS_SCALE_ITEM.key}
            label={IS_SCALE_ITEM.label}
            disabled
          />

          <TextInput
            register={register}
            name={PLU.key}
            label={PLU.label}
            disabled
          />

          <TextInput
            register={register}
            name={FRESH_CATEGORIES.key}
            label={FRESH_CATEGORIES.label}
            disabled
          />

          <TextInput
            register={register}
            name={QUANTITY_STEP.key}
            label={QUANTITY_STEP.label}
            disabled
          />

          <TextInput
            register={register}
            name={UNIT.key}
            label={UNIT.label}
            disabled
          />

          {/* LAST_DISABLE_TIME */}
          <TextInput
            register={register}
            name={LAST_DISABLE_TIME.key}
            label={LAST_DISABLE_TIME.label}
            disabled
          />

          {/* Created At */}
          <TextInput
            register={register}
            name={CREATED_AT.key}
            label={CREATED_AT.label}
            disabled
          />
          {/* Updated At */}
          <TextInput
            register={register}
            name={UPDATED_AT.key}
            label={UPDATED_AT.label}
            disabled
          />
          {/* Hide Aggregators */}
          <AggregatorsDropdown
            defaultSelected={
              catalogue?.hideAggregators ??
              catalogue?.[HIDE_AGGREGATORS.key] ??
              []
            }
            resetForm={false}
            error={errors?.[HIDE_AGGREGATORS.key]?.message as string}
            setValue={setValue}
            name={HIDE_AGGREGATORS.key}
            label={HIDE_AGGREGATORS.label}
            isActive
          />
          {/* Activated Stores */}
          <MultiSelectStoreDropdown
            defaultSelected={catalogue?.[ACTIVATED_STORES.key] ?? []}
            resetForm={false}
            error={errors?.[ACTIVATED_STORES.key]?.message as string}
            setValue={setValue}
            name={ACTIVATED_STORES.key}
            label={ACTIVATED_STORES.label}
            hintText="Stores where this product is enabled. Controls which stores can receive this item via Deliveroo listing, price, and inventory sync."
          />
          {/* In Stock Stores - normally maintained automatically by
          inventory sync based on ERP stock; only edit manually for an
          initial backfill or a deliberate override. Restricted to stores
          currently selected in Activated Stores - see
          selectedActivatedStores. */}
          <MultiSelectStoreDropdown
            defaultSelected={catalogue?.[IN_STOCK_STORES.key] ?? []}
            resetForm={false}
            error={errors?.[IN_STOCK_STORES.key]?.message as string}
            setValue={setValue}
            name={IN_STOCK_STORES.key}
            label={IN_STOCK_STORES.label}
            allowedValues={selectedActivatedStores}
            hintText="Auto-managed by inventory sync based on real-time ERP stock. Do not edit manually unless doing an initial backfill or a deliberate override — inventory sync will overwrite this the next time stock changes."
          />
          {/* Special Category */}
          <MultiSelectDropdown
            name={SPECIAL_CATEGORY.key}
            label={SPECIAL_CATEGORY.label}
            setValue={setValue}
            defaultSelected={catalogue?.[SPECIAL_CATEGORY.key]}
            error={errors?.[SPECIAL_CATEGORY.key]?.message as string}
            options={specialCategoryOptions}
          />
          {/* Online Description */}
          <TextInput
            register={register}
            name={ONLINE_DESC.key}
            label={ONLINE_DESC.label}
          />
          {/* Online Pack Info */}
          <TextInput
            register={register}
            name={ONLINE_PACK_INFO.key}
            label={ONLINE_PACK_INFO.label}
          />
        </div>

        <hr className="my-5 border-gray-100" />

        <div className="grid gap-x-8 divide-y divide-gray-100 md:grid-cols-3">
          <SwitchField
            control={control}
            name={ACTIVE_ONLINE.key}
            label={ACTIVE_ONLINE.label}
            description="Activates this product on our portal"
          />
          <SwitchField
            control={control}
            name={BLOCK_ERP_PRICE_SYNC.key}
            label={BLOCK_ERP_PRICE_SYNC.label}
            description="Ignores and blocks the price received from the ERP"
          />
          <SwitchField
            control={control}
            name={IS_FRESH_PREPARATION.key}
            label={IS_FRESH_PREPARATION.label}
            description="Sends this product to the KDS system"
          />
          <SwitchField
            control={control}
            name={IS_AVAILABLE_IN_FRESH.key}
            label={IS_AVAILABLE_IN_FRESH.label}
            description="Includes this product in the Fresh system's get API"
            disabled
          />
          <SwitchField
            control={control}
            name={IS_DISABLE.key}
            label={IS_DISABLE.label}
            description="Treats this product as out of stock"
            disabled
          />
        </div>

        <hr className="my-5 border-gray-100" />

        <div className="grid gap-x-8 md:grid-cols-3">
          <TextInput
            register={register}
            name={IS_THIRD_PARTY_PRODUCT.key}
            label={IS_THIRD_PARTY_PRODUCT.label}
            disabled
          />

          <MultiSelectStoreDropdown
            defaultSelected={catalogue?.stores ?? catalogue?.[STORES.key] ?? []}
            resetForm={false}
            error={errors?.[STORES.key]?.message as string}
            setValue={setValue}
            name={STORES.key}
            label={STORES.label}
            isActive
            // this mapping exists only for third-party products, so it lists
            // third-party stores exclusively (inverse of every other dropdown)
            isThirdPartyStore
            disabled={!catalogue?.isThirdPartyProduct}
          />

          <MultiSelectChainDropdown
            defaultSelected={catalogue?.chains ?? catalogue?.[CHAINS.key] ?? []}
            resetForm={false}
            error={errors?.[CHAINS.key]?.message as string}
            setValue={setValue}
            name={CHAINS.key}
            label={CHAINS.label}
            isActive
            disabled={!catalogue?.isThirdPartyProduct}
          />
        </div>

        {/* Classification */}
        {catalogue?.classifications?.length > 0 ? (
          <div className="mt-3">
            <span className="mb-1 block text-sm text-gray-900">
              Classifications
            </span>

            <div className="flex flex-wrap gap-2">
              {catalogue.classifications.map((item: string) => (
                <span
                  key={item}
                  className="rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-medium capitalize text-gray-700"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
      </FormLayout>

      {isBundleProduct && (
        <>
          <h3 className="title mb-3 mt-4">Bundle Pricing</h3>
          <FormLayout>
            <BundlePricingFields register={register} errors={errors} />
          </FormLayout>

          <h3 className="title mb-3 mt-4">Products</h3>
          <FormLayout>
            <BundleItemsFields
              control={control}
              register={register}
              errors={errors}
              initialProducts={initialProducts}
            />
          </FormLayout>
        </>
      )}

      <div className="my-4 flex justify-end gap-5">
        <Button
          buttonText="Save"
          form="master-data-update-form"
          className="w-32"
        />
        <Button
          buttonText="cancel"
          variant="outline"
          type="button"
          onClick={backToMasterData}
          className="w-32"
        />
      </div>
    </form>
  )
}

export default MasterDataUpdateForm
